import type { BookImportJob, BookPayment } from "./api"
import { importText } from "./book-imports"

export type StatementPaymentReference = {
  id: string; row: string; bank: string; date: string; amount: string;
  currency: string; reference?: string; evidenced: boolean
}

// Normalize the two saved statement-review formats. These are reported
// references, not authority to create, suppress or link accounting entries.
export function statementPaymentReferences(job: BookImportJob): StatementPaymentReference[] {
  const result = new Map<string, StatementPaymentReference>()
  for (const item of job.items) {
    if (item.kind !== "statement" || item.status === "dismissed") continue
    for (const field of ["matching_draft_payments", "existing_payment_matches"]) {
      const matches = item.data[field]
      if (!Array.isArray(matches)) continue
      for (const entry of matches) {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue
        const ids = new Set([entry.existing_draft_payment_id, entry.matched_draft_payment_id, entry.existing_payment_id, entry.payment_id]
          .filter((value): value is string => typeof value === "string" && !!value.trim()))
        const evidence: unknown[] = Array.isArray(entry.evidence) ? entry.evidence : [entry.evidence]
        const row: StatementPaymentReference = {
          id: ids.size === 1 ? [...ids][0] : "", row: importText(entry, "source_row_key"),
          bank: importText(item.data, "bank_account_code"), date: importText(entry, "date"),
          amount: importText(entry, "signed_amount"), currency: importText(item.data, "currency"),
          reference: typeof entry.bank_reference === "string" ? entry.bank_reference : undefined,
          evidenced: evidence.some((value) => typeof value === "string" && value.startsWith(`file:${job.file_id}:`)),
        }
        result.set(JSON.stringify(row), row)
      }
    }
  }
  return [...result.values()]
}

function minor(value: string): bigint | undefined {
  const normalized = value.trim().replace(/[\s\u00a0\u202f]/g, "")
  if (!/^[+-]?\d{1,16}(?:\.\d{1,2})?$/.test(normalized)) return
  const [whole, fraction = ""] = normalized.replace(/^[+-]/, "").split(".")
  return (BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"))) * (normalized.startsWith("-") ? -1n : 1n)
}

export function verifyStatementPaymentReferences(references: StatementPaymentReference[], payments: BookPayment[], periodID: string) {
  const byID = new Map(payments.map((payment) => [payment.id, payment]))
  const rows = new Map<string, number>(), ids = new Map<string, number>()
  for (const reference of references) {
    rows.set(reference.row, (rows.get(reference.row) ?? 0) + 1)
    ids.set(reference.id, (ids.get(reference.id) ?? 0) + 1)
  }
  const verified: BookPayment[] = []
  for (const reference of references) {
    const payment = byID.get(reference.id), amount = minor(reference.amount)
    if (!payment || !reference.evidenced || !reference.row || rows.get(reference.row) !== 1 || ids.get(reference.id) !== 1 ||
        !reference.bank || payment.period_id !== periodID || payment.bank_account_code !== reference.bank ||
        payment.payment_date !== reference.date || payment.currency !== "EUR" || (reference.currency && reference.currency !== "EUR") ||
        !["draft", "confirmed"].includes(payment.status) || amount === undefined || amount === 0n ||
        (reference.reference !== undefined && payment.reference_number !== reference.reference)) continue
    const saved = minor(payment.amount)
    if (saved === undefined || saved <= 0n || amount !== (payment.direction === "incoming" ? saved : -saved)) continue
    verified.push(payment)
  }
  return { payments: verified, unverified: references.length - verified.length }
}
